import { useEffect, useState, useCallback } from "react";
import WindowSearch from "@/components/WindowSearch";
import { toast } from "sonner";
import { Loader2, RefreshCw, Save, History, RotateCcw, Plus, X, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const SCOPE_LABEL = { base: "Base", output_type: "Output Type", category: "Category", aspect: "Aspect" };
const SCOPE_COLOR = {
  base: "text-[#00F0FF]", output_type: "text-[#E1306C]", category: "text-[#a78bfa]", aspect: "text-[#FFCF80]",
};

export default function InstagramPromptManager() {
  const [scopes, setScopes] = useState([]);
  const [meta, setMeta] = useState({ output_types: [], output_labels: {} });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // currently-edited scope key
  const [draft, setDraft] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [versions, setVersions] = useState(null); // {scope, list}
  const [showAdd, setShowAdd] = useState(false);
  const [newScope, setNewScope] = useState({ scope_type: "category", scope_value: "", content: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.igAdminPrompts();
      setScopes(r.scopes || []);
      setMeta({ output_types: r.output_types || [], output_labels: r.output_labels || {} });
    } catch { toast.error("Could not load prompts."); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const key = (s) => `${s.scope_type}:${s.scope_value}`;
  const beginEdit = (s) => { setActive(key(s)); setDraft(s.active?.content || ""); setNote(""); setVersions(null); };

  const save = async (s) => {
    if (!draft.trim()) { toast.error("Prompt content can't be empty."); return; }
    setSaving(true);
    try {
      await api.igAdminSavePrompt({ scope_type: s.scope_type, scope_value: s.scope_value, content: draft, note });
      toast.success(`Saved — new version created for ${SCOPE_LABEL[s.scope_type]} · ${s.scope_value}`);
      setActive(null); setNote(""); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    setSaving(false);
  };

  const openHistory = async (s) => {
    try {
      const list = await api.igAdminPromptVersions(s.scope_type, s.scope_value);
      setVersions({ scope: s, list });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load history."); }
  };

  const restore = async (versionId) => {
    try {
      await api.igAdminRestorePrompt(versionId);
      toast.success("Restored version");
      setVersions(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed."); }
  };

  const addScope = async () => {
    const sv = newScope.scope_type === "output_type" ? newScope.scope_value : newScope.scope_value.trim();
    if (!sv) { toast.error("Enter a scope value."); return; }
    if (!newScope.content.trim()) { toast.error("Enter prompt content."); return; }
    try {
      await api.igAdminSavePrompt({ scope_type: newScope.scope_type, scope_value: sv, content: newScope.content, note: "Created scope" });
      toast.success("Prompt scope added");
      setShowAdd(false); setNewScope({ scope_type: "category", scope_value: "", content: "" }); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add."); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#E1306C]" /></div>;

  const grouped = { base: [], output_type: [], category: [], aspect: [] };
  scopes.forEach((s) => { (grouped[s.scope_type] || (grouped[s.scope_type] = [])).push(s); });

  return (
    <div data-testid="instagram-prompt-manager">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 font-display text-lg font-black text-white"><Sparkles size={18} className="text-[#a78bfa]" /> AI Prompt Manager</h2>
          <p className="max-w-2xl text-xs text-[#A1A1AA]">Versioned default AI instructions assembled into the V2 generation box, per Base → Output Type → Category → Aspect. Editing saves a new version; you can restore any earlier one.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowAdd(true)} data-testid="igpm-add-scope" className="flex items-center gap-1.5 rounded-lg border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 px-3 py-1.5 text-xs font-bold text-[#a78bfa] hover:bg-[#8B5CF6]/20"><Plus size={13} /> Add Prompt</button>
          <button onClick={load} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white"><RefreshCw size={13} /> Refresh</button>
        </div>
      </div>

      {["base", "output_type", "category", "aspect"].map((st) => (
        (grouped[st] && grouped[st].length > 0) && (
          <div key={st} className="mb-6">
            <div className={`mb-2 text-[11px] font-bold uppercase tracking-widest ${SCOPE_COLOR[st]}`}>{SCOPE_LABEL[st]}</div>
            <div className="space-y-2">
              {grouped[st].map((s) => {
                const editing = active === key(s);
                const label = st === "output_type" ? (meta.output_labels[s.scope_value] || s.scope_value) : s.scope_value;
                return (
                  <div key={key(s)} data-testid={`igpm-scope-${st}-${s.scope_value}`} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white">{label}</span>
                        <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] font-bold text-[#A1A1AA]" data-testid={`igpm-version-${st}-${s.scope_value}`}>v{s.active?.version || 1}</span>
                        {s.version_count > 1 && <span className="text-[10px] text-[#666]">{s.version_count} versions</span>}
                      </div>
                      <div className="flex gap-1.5">
                        <button onClick={() => openHistory(s)} data-testid={`igpm-history-${st}-${s.scope_value}`} className="flex items-center gap-1 rounded-lg border border-white/15 px-2 py-1 text-[11px] font-semibold text-[#A1A1AA] hover:text-white"><History size={12} /> History</button>
                        {!editing && <button onClick={() => beginEdit(s)} data-testid={`igpm-edit-${st}-${s.scope_value}`} className="rounded-lg bg-[#E1306C] px-2.5 py-1 text-[11px] font-bold text-white hover:bg-[#E1306C]/90">Edit</button>}
                      </div>
                    </div>
                    {editing ? (
                      <div className="mt-2">
                        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={6} data-testid={`igpm-textarea-${st}-${s.scope_value}`}
                          className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-xs leading-relaxed text-white outline-none focus:border-[#8B5CF6]/60" />
                        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Change note (optional)" data-testid={`igpm-note-${st}-${s.scope_value}`}
                          className="mt-2 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-1.5 text-xs text-white outline-none focus:border-[#8B5CF6]/60" />
                        <div className="mt-2 flex gap-2">
                          <button onClick={() => save(s)} disabled={saving} data-testid={`igpm-save-${st}-${s.scope_value}`} className="flex items-center gap-1.5 rounded-lg bg-[#22c55e] px-3 py-1.5 text-xs font-bold text-black disabled:opacity-50">{saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save New Version</button>
                          <button onClick={() => setActive(null)} className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA]">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <p className="mt-1.5 line-clamp-2 whitespace-pre-wrap text-xs text-[#A1A1AA]">{s.active?.content}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )
      ))}

      {versions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="igpm-history-modal" onClick={() => setVersions(null)}>
          <div className="relative max-h-[80vh] w-full max-w-2xl overflow-auto rounded-2xl border border-white/10 bg-[#121212] p-5" onClick={(e) => e.stopPropagation()}>
            <WindowSearch />
            <div className="mb-3 flex items-center justify-between">
              <div className="font-display text-lg font-black text-white">Version history · {SCOPE_LABEL[versions.scope.scope_type]} · {versions.scope.scope_value}</div>
              <button onClick={() => setVersions(null)} className="text-[#A1A1AA] hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-2">
              {versions.list.map((v) => (
                <div key={v.id} data-testid={`igpm-hist-v${v.version}`} className={`rounded-xl border p-3 ${v.is_active ? "border-[#22c55e]/50 bg-[#22c55e]/[0.06]" : "border-white/10 bg-white/[0.02]"}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-bold text-white">v{v.version}{v.is_active && <span className="rounded-full bg-[#22c55e]/20 px-2 py-0.5 text-[10px] font-bold text-[#22c55e]">Active</span>}</div>
                    {!v.is_active && <button onClick={() => restore(v.id)} data-testid={`igpm-restore-v${v.version}`} className="flex items-center gap-1 rounded-lg bg-[#E1306C] px-2.5 py-1 text-[11px] font-bold text-white hover:bg-[#E1306C]/90"><RotateCcw size={12} /> Restore</button>}
                  </div>
                  {v.note && <div className="mt-0.5 text-[11px] italic text-[#666]">{v.note}</div>}
                  <p className="mt-1.5 whitespace-pre-wrap text-xs text-[#A1A1AA]">{v.content}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="igpm-add-modal" onClick={() => setShowAdd(false)}>
          <div className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[#121212] p-5" onClick={(e) => e.stopPropagation()}>
            <WindowSearch />
            <div className="mb-3 font-display text-lg font-black text-white">Add a prompt scope</div>
            <label className="mb-1 block text-xs font-semibold text-[#A1A1AA]">Scope type</label>
            <select value={newScope.scope_type} onChange={(e) => setNewScope((n) => ({ ...n, scope_type: e.target.value, scope_value: "" }))} data-testid="igpm-add-type"
              className="mb-2 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none">
              <option value="category">Category</option>
              <option value="output_type">Output Type</option>
              <option value="aspect">Aspect</option>
            </select>
            <label className="mb-1 block text-xs font-semibold text-[#A1A1AA]">Scope value</label>
            {newScope.scope_type === "output_type" ? (
              <select value={newScope.scope_value} onChange={(e) => setNewScope((n) => ({ ...n, scope_value: e.target.value }))} data-testid="igpm-add-value"
                className="mb-2 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none">
                <option value="">Select…</option>
                {meta.output_types.map((o) => <option key={o} value={o}>{meta.output_labels[o] || o}</option>)}
              </select>
            ) : (
              <input value={newScope.scope_value} onChange={(e) => setNewScope((n) => ({ ...n, scope_value: e.target.value }))} data-testid="igpm-add-value"
                placeholder={newScope.scope_type === "aspect" ? "e.g. 4:5" : "e.g. Business"}
                className="mb-2 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none" />
            )}
            <label className="mb-1 block text-xs font-semibold text-[#A1A1AA]">Prompt content</label>
            <textarea value={newScope.content} onChange={(e) => setNewScope((n) => ({ ...n, content: e.target.value }))} rows={5} data-testid="igpm-add-content"
              className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-xs text-white outline-none" />
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setShowAdd(false)} className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-[#A1A1AA]">Cancel</button>
              <button onClick={addScope} data-testid="igpm-add-save" className="rounded-lg bg-[#E1306C] px-4 py-2 text-sm font-bold text-white">Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
